<template>
  <div class="currency-input">
    <span v-if="label" class="currency-input__label">
      {{ label }}
    </span>

    <ControlWrapper
      ref="controlWrapperRef"
      :isInvalid="!!error || isInvalid"
      :disabled="disabled"
      :addonAfter="addonAfter"
    >
      <template #default="{ onFocus, onBlur }">
        <NumberInput
          :disabled="disabled"
          :value="state.price"
          :hasBorder="false"
          :allowClear="allowClear"
          :placeholder="placeholder"
          :size="size"
          autocomplete="off"
          @focus="handleFocus(onFocus)"
          @blur="onBlur"
          @input="handleChange"
          @update:value="setPricesByValue"
        />
      </template>

      <template #divider>
        <Divider class="currency-input__divider" type="vertical" />
      </template>

      <template #suffix>
        <Select
          v-if="!disabledCurrencyChange"
          v-model:value="state.currency"
          class="currency-input__select"
          :disabled="disabled"
          :options="currencyOptions"
          :showArrow="showSelectArrow"
          :hasBorder="false"
          @change="(value) => handleCurrencySelectChange(value as ECurrency)"
        />
      </template>
    </ControlWrapper>

    <span
      v-if="!!error"
      class="currency-input__input-help currency-input__input-help_error"
    >
      {{ error }}
    </span>

    <span
      v-else-if="help"
      class="currency-input__input-help"
    >
      {{ help }}
    </span>
  </div>
</template>

<script lang="ts">
import { Divider } from 'ant-design-vue';
import {
  defineComponent,
  onMounted,
  PropType,
  reactive,
  ref,
  UnwrapNestedRefs,
  watch,
} from 'vue';

import { ECurrency } from '@/constants';
import {
  ControlWrapper,
  NumberInput,
  Select,
} from '@/ui';
import { ESize, TSelectOption } from '@/ui/types';
import tt from '@/i18n/utils/translateText';
import { useTranslateCurrency } from '@/composables/useTranslateCurrency';

/* CurrencyInput - компонент для ввода цены с выбором валюты из селекта */
export default defineComponent({
  name: 'CurrencyInput',
  components: {
    ControlWrapper,
    NumberInput,
    Select,
    Divider,
  },
  props: {
    value: {
      type: [Number, null] as PropType<number | null>,
      default: null,
    },
    currency: {
      type: String as PropType<ECurrency>,
      default: ECurrency.rub,
    },
    label: {
      type: String,
      default: '',
    },
    addonAfter: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    help: {
      type: String,
      default: '',
    },
    error: {
      type: [String, null] as PropType<string | null>,
      default: '',
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    disabledCurrencyChange: {
      type: Boolean,
      required: false,
    },
    allowClear: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String as PropType<ESize>,
      default: ESize.medium,
    },
    showSelectArrow: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['input', 'focus'],
  setup(props, { emit, expose }) {
    const { getCurrencySymbol, getCurrencyName } = useTranslateCurrency();

    const controlWrapperRef = ref<HTMLElement | null>(null);

    const state: UnwrapNestedRefs<{
      price: number | null,
      currency?: ECurrency,
    }> = reactive({
      price: null,
      currency: props.currency,
    });

    const currencyOptionsOld = Object.values(ECurrency).map((value: ECurrency) => (
      {
        value,
        name: getCurrencyName(value),
        label: getCurrencySymbol(value),
      }));

    const currencyOptions: TSelectOption[] = Object.values(ECurrency)
      .map((value: ECurrency) => ({
        value,
        label: getCurrencySymbol(value),
      }));

    const setPricesByValue = (value: number | null) => {
      state.price = value;
    };

    const handleFocus = (onFocus: () => void) => {
      onFocus();
      emit('focus');
    };

    const handleChange = (value: number) => {
      emit('input', value, state.currency);
    };

    const handleCurrencySelectChange = (currency: ECurrency) => {
      emit('input', state.price, currency);
    };

    watch(() => props.value, setPricesByValue);
    watch(() => props.currency, (newCurrency: ECurrency) => {
      state.currency = newCurrency;
    });

    onMounted(() => {
      setPricesByValue(props.value);
    });

    expose({ inputRef: controlWrapperRef });

    return {
      ECurrency,
      state,
      currencyOptions,
      controlWrapperRef,
      currencyOptionsOld,
      setPricesByValue,

      tt,
      handleChange,
      handleFocus,
      handleCurrencySelectChange,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
